import apiClient from './api';
import {
  InstanceAnalytics,
  PostAnalytics,
  CampaignAnalytics,
  RecordAnalyticsData,
  AnalyticsData,
  AnalyticsHubFilters,
  AnalyticsHubResponse,
  AnalyticsHubDetail,
} from '@/types/analytics';

/**
 * Analytics Service
 *
 * API calls for analytics management
 */

// Widget-specific types
export interface BestTimesData {
  platform: string;
  bestDays: number[];
  bestHours: number[];
  avgEngagementRate: number;
  lastCalculatedAt: string | null;
}

export interface AIInsightsData {
  platform: string;
  insights: {
    summary: string;
    recommendations: string[];
    topPerformingContentTypes?: string[];
    engagementTrends?: string;
  } | null;
  generatedAt: string | null;
}

/**
 * Result of POST /api/analytics/insights/generate.
 *
 * - `status: 'insufficient_data'` → team has fewer than `minRequired` posts
 *   in the last 30 days; no job was queued (`postsFound`/`minRequired` set).
 * - `status: 'queued'` → job enqueued (`jobId` set); poll `getAIInsights`.
 */
export interface GenerateAIInsightsResult {
  status: 'queued' | 'insufficient_data';
  jobId?: string;
  postsFound?: number;
  minRequired?: number;
}

export const analyticsService = {
  /**
   * Get analytics for a scheduled instance (all channels)
   */
  getInstanceAnalytics: async (instanceId: number): Promise<InstanceAnalytics> => {
    const response = await apiClient.get(`/api/analytics/instance/${instanceId}`);
    return response.data.data;
  },

  /**
   * Get aggregated analytics for a post (all publishes)
   */
  getPostAnalytics: async (postId: number): Promise<PostAnalytics> => {
    const response = await apiClient.get(`/api/analytics/post/${postId}`);
    return response.data.data;
  },

  /**
   * Get aggregated analytics for a variation
   */
  getVariationAnalytics: async (variationId: number): Promise<AnalyticsData> => {
    const response = await apiClient.get(`/api/analytics/variation/${variationId}`);
    return response.data.data;
  },

  /**
   * Get campaign analytics (all posts in campaign)
   */
  getCampaignAnalytics: async (campaignId: number): Promise<CampaignAnalytics> => {
    const response = await apiClient.get(`/api/analytics/campaign/${campaignId}`);
    return response.data.data;
  },

  /**
   * Record analytics manually (internal/webhook use)
   */
  recordAnalytics: async (data: RecordAnalyticsData): Promise<void> => {
    await apiClient.post('/api/analytics/record', data);
  },

  /**
   * Get best posting times for the team
   */
  getBestTimes: async (platform?: string): Promise<BestTimesData[]> => {
    const params = new URLSearchParams();
    if (platform && platform !== 'all') {
      params.append('platform', platform);
    }
    const response = await apiClient.get(`/api/analytics/best-times?${params.toString()}`);
    return response.data.data;
  },

  /**
   * Get AI insights for the team
   */
  getAIInsights: async (platform?: string): Promise<AIInsightsData[]> => {
    const params = new URLSearchParams();
    if (platform && platform !== 'all') {
      params.append('platform', platform);
    }
    const response = await apiClient.get(`/api/analytics/insights?${params.toString()}`);
    return response.data.data;
  },

  /**
   * Generate AI insights.
   *
   * The backend performs a synchronous floor check before queuing:
   * - `insufficient_data`: team has fewer than `minRequired` posts in the last
   *   30 days — no job was queued, caller should not poll.
   * - `queued`: the job was enqueued; caller should poll `getAIInsights`
   *   until a fresh `generatedAt` appears.
   */
  generateAIInsights: async (): Promise<GenerateAIInsightsResult> => {
    const response = await apiClient.post('/api/analytics/insights/generate');
    return response.data.data;
  },

  /**
   * Get Analytics Hub data with filters
   */
  getHubData: async (filters: AnalyticsHubFilters): Promise<AnalyticsHubResponse> => {
    const params = new URLSearchParams();
    params.append('page', String(filters.page));
    params.append('limit', String(filters.limit));
    params.append('sortBy', filters.sortBy);
    params.append('sortOrder', filters.sortOrder);
    params.append('from', filters.from);
    params.append('to', filters.to);
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.channelId) params.append('channelId', String(filters.channelId));
    const response = await apiClient.get(`/api/analytics/hub?${params.toString()}`);
    return response.data.data;
  },

  /**
   * Get Analytics Hub detail for a specific channel instance
   */
  getHubDetail: async (channelInstanceId: number): Promise<AnalyticsHubDetail> => {
    const response = await apiClient.get(`/api/analytics/hub/${channelInstanceId}`);
    return response.data.data;
  },

};
